import { useEffect, useRef, useState } from 'react';
// UI
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

/**
 * iOS Safari/WKWebView zooms the page when focusing an input with font-size < 16px.
 * emoji-mart defaults to --font-size: 15px and search inputs use calc(var(--font-size) - 1px)
 * (= 14px). Inject into the shadow root so search stays at least 16px.
 */
const IOS_NO_ZOOM_STYLE = `
  :host { --font-size: 16px; }
  .search input,
  .search button {
    font-size: 16px !important;
  }
`;

const injectIosNoZoomStyle = (pickerEl) => {
  if (!pickerEl?.shadowRoot) return;
  if (pickerEl.shadowRoot.querySelector('[data-cbc-ios-no-zoom]')) return;
  const style = document.createElement('style');
  style.setAttribute('data-cbc-ios-no-zoom', '');
  style.textContent = IOS_NO_ZOOM_STYLE;
  pickerEl.shadowRoot.appendChild(style);
};

/**
 * Slack-style full emoji picker (search + categories + all standard emojis).
 *
 * Instantiates emoji-mart into a host div via the `ref` (light-DOM) API, and
 * always constructs from `customElements.get('em-emoji-picker')` after the
 * first load so re-opening never throws "Illegal constructor" on iOS WKWebView.
 *
 * Data is lazy-loaded so the ~1.5MB dataset stays out of the main bundle.
 *
 * @param {(emoji: string) => void} onSelect - called with the native emoji character.
 */
const EmojiPicker = ({
  onSelect,
  dynamicWidth = true,
  autoFocus = false,
  /** When true, picker fills its parent instead of using a fixed 50vh max height. */
  fillHeight = false,
}) => {
  const hostRef = useRef(null);
  const theme = useTheme();
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const [loadError, setLoadError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    if (!host) return undefined;

    // Clear any previous picker instance from this host.
    host.replaceChildren();
    setReady(false);
    setLoadError(null);

    (async () => {
      try {
        const [{ Picker }, dataModule] = await Promise.all([
          import('emoji-mart'),
          import('@emoji-mart/data'),
        ]);
        if (cancelled || !hostRef.current) return;

        const props = {
          data: dataModule.default,
          onEmojiSelect: (emoji) => onSelectRef.current?.(emoji.native),
          theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
          set: 'native',
          previewPosition: 'none',
          skinTonePosition: 'search',
          navPosition: 'top',
          dynamicWidth,
          autoFocus,
          maxFrequentRows: 2,
          // Frame mode: render into our host div instead of as a standalone CE.
          ref: hostRef,
        };

        // Prefer the already-registered custom element constructor. Calling
        // `new Picker()` again after define() throws Illegal constructor on
        // some WebViews (notably iOS Capacitor).
        const Registered = window.customElements?.get('em-emoji-picker');
        if (Registered) {
          // eslint-disable-next-line no-new
          new Registered(props);
        } else {
          // eslint-disable-next-line no-new
          new Picker(props);
        }

        const pickerEl =
          hostRef.current?.querySelector('em-emoji-picker') ||
          hostRef.current?.firstElementChild;
        injectIosNoZoomStyle(pickerEl);

        if (!cancelled) setReady(true);
      } catch (err) {
        console.error('Failed to load emoji picker:', err);
        if (!cancelled) setLoadError('Could not load emoji picker');
      }
    })();

    return () => {
      cancelled = true;
      if (hostRef.current) {
        hostRef.current.replaceChildren();
      }
    };
  }, [theme.palette.mode, dynamicWidth, autoFocus]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: fillHeight ? 'stretch' : 'center',
        width: '100%',
        minHeight: fillHeight ? 0 : 360,
        height: fillHeight ? '100%' : 'auto',
        // Avoid double-tap zoom on the picker surface (iOS Safari).
        touchAction: 'manipulation',
        '& em-emoji-picker': {
          width: '100%',
          ...(fillHeight
            ? { height: '100%', maxHeight: 'none' }
            : { height: 'min(435px, 70vh)', maxHeight: '70vh' }),
        },
      }}
    >
      {!ready && !loadError && (
        <CircularProgress
          size={24}
          sx={{ position: 'absolute', top: '50%', marginTop: '-12px' }}
        />
      )}
      {loadError && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {loadError}
        </Typography>
      )}
      <Box
        ref={hostRef}
        sx={{
          width: '100%',
          height: fillHeight ? '100%' : 'auto',
          minHeight: fillHeight ? 0 : undefined,
        }}
      />
    </Box>
  );
};

export default EmojiPicker;
